// src/Components/CommunitySettingsModal/SchemaHierarchyEditor.tsx
//
// DEV-920 (C2). Hierarchy editor for community-defined schema hierarchies.
// Composes per-tier UX (pick a field, set its order) over the underlying
// schema_hierarchies row's level_order array.
//
// Constraints (mirrored from the backend):
//   - Only `guid` fields are eligible as hierarchy tiers; other field
//     types don't reference entities and can't anchor a hierarchy walk.
//   - Maximum 6 tiers (level_order_too_deep at the controller layer).
//   - value_source is fixed to 'metadata_value' for community-created
//     hierarchies. entity_walk hierarchies (Geography) are platform-
//     seeded and don't go through this UI.
//   - hierarchyKey is immutable after create (stable identifier for
//     code-side lookups).
//
// Save flow: POST /api/CommunityMetadata/schemas/{schemaGuid}/hierarchies
// (create) or PUT (edit). Server validates everything; this UI does shape
// checks to fail fast and surface friendly errors.

import { useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  createSchemaHierarchy,
  updateSchemaHierarchy,
} from "../../api";
import type {
  CollectionSchemaFieldResponse,
  SchemaHierarchyResponse,
  CreateSchemaHierarchyRequest,
  UpdateSchemaHierarchyRequest,
} from "../../api";
import styles from "./CommunitySettingsModal.module.css";

const MAX_TIERS = 6;

export interface SchemaHierarchyEditorProps {
  mode: "create" | "edit";
  schemaGuid: string;
  schemaFields: CollectionSchemaFieldResponse[];
  /** Required when mode === "edit". */
  existing?: SchemaHierarchyResponse;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}

export function SchemaHierarchyEditor(props: SchemaHierarchyEditorProps) {
  const { mode, schemaGuid, schemaFields, existing, onClose, onSaved } = props;
  const { t } = useAppTranslation("community");

  const initialOrder = useMemo<string[]>(() => {
    if (!existing?.levelOrder) return [];
    try {
      const parsed = JSON.parse(existing.levelOrder);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }, [existing?.levelOrder]);

  const [hierarchyKey, setHierarchyKey] = useState<string>(
    existing?.hierarchyKey ?? "",
  );
  const [displayName, setDisplayName] = useState<string>(
    existing?.displayName ?? "",
  );
  const [presentation, setPresentation] = useState<string>(
    existing?.presentation ?? "cascading",
  );
  const [orderedFieldGuids, setOrderedFieldGuids] =
    useState<string[]>(initialOrder);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);

  // If parent re-renders with a different `existing`, re-seed the form.
  useEffect(() => {
    setHierarchyKey(existing?.hierarchyKey ?? "");
    setDisplayName(existing?.displayName ?? "");
    setPresentation(existing?.presentation ?? "cascading");
    setOrderedFieldGuids(initialOrder);
    setError(null);
  }, [existing, initialOrder]);

  const guidFields = useMemo(
    () => schemaFields.filter((f) => f.type === "guid"),
    [schemaFields],
  );

  const eligibleToAdd = useMemo(
    () => guidFields.filter((f) => !orderedFieldGuids.includes(f.fieldGuid)),
    [guidFields, orderedFieldGuids],
  );

  const fieldByGuid = useMemo(() => {
    const m: Record<string, CollectionSchemaFieldResponse> = {};
    for (const f of schemaFields) m[f.fieldGuid] = f;
    return m;
  }, [schemaFields]);

  const addField = (fieldGuid: string) => {
    if (!fieldGuid) return;
    if (orderedFieldGuids.length >= MAX_TIERS) {
      setError(t("hierarchyEditor.errors.tooManyTiers", { max: MAX_TIERS }));
      return;
    }
    setOrderedFieldGuids([...orderedFieldGuids, fieldGuid]);
    setError(null);
  };

  const removeAt = (index: number) => {
    const next = orderedFieldGuids.slice();
    next.splice(index, 1);
    setOrderedFieldGuids(next);
  };

  const swap = (a: number, b: number) => {
    if (a < 0 || b < 0 || a >= orderedFieldGuids.length || b >= orderedFieldGuids.length) return;
    const next = orderedFieldGuids.slice();
    [next[a], next[b]] = [next[b], next[a]];
    setOrderedFieldGuids(next);
  };

  const onSave = async () => {
    if (!displayName.trim()) {
      setError(t("hierarchyEditor.errors.displayNameRequired"));
      return;
    }
    if (mode === "create" && !hierarchyKey.trim()) {
      setError(t("hierarchyEditor.errors.hierarchyKeyRequired"));
      return;
    }
    if (orderedFieldGuids.length === 0) {
      setError(t("hierarchyEditor.errors.atLeastOneTier"));
      return;
    }
    if (orderedFieldGuids.length > MAX_TIERS) {
      setError(t("hierarchyEditor.errors.tooManyTiers", { max: MAX_TIERS }));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const levelOrderJson = JSON.stringify(orderedFieldGuids);
      const leafFieldGuid =
        orderedFieldGuids[orderedFieldGuids.length - 1] ?? null;

      if (mode === "create") {
        const body: CreateSchemaHierarchyRequest = {
          hierarchyKey: hierarchyKey.trim(),
          displayName: displayName.trim(),
          leafFieldGuid,
          levelOrder: levelOrderJson,
          presentation,
          valueSource: "metadata_value",
        };
        await createSchemaHierarchy(schemaGuid, body);
      } else if (existing) {
        const body: UpdateSchemaHierarchyRequest = {
          displayName: displayName.trim(),
          leafFieldGuid,
          levelOrder: levelOrderJson,
          presentation,
        };
        await updateSchemaHierarchy(schemaGuid, existing.hierarchyGuid, body);
      }
      await onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.message || t("hierarchyEditor.errors.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent} style={{ minInlineSize: 480 }}>
        <h3>
          {mode === "create"
            ? t("hierarchyEditor.modal.create")
            : t("hierarchyEditor.modal.edit")}
        </h3>

        {error && (
          <div className={styles.schemaEditorErrorBanner}>
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)}>
              {t("common.dismiss")}
            </button>
          </div>
        )}

        <div className={styles.field}>
          <label className={styles.label}>
            {t("hierarchyEditor.displayName")}
          </label>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={t("hierarchyEditor.displayNamePlaceholder")}
            maxLength={64}
            autoFocus
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t("hierarchyEditor.hierarchyKey")}
            {mode === "edit" && (
              <span
                className={styles.schemaEditorPlatformNote}
                style={{ marginInlineStart: 8 }}
              >
                ({t("schemaEditor.keyImmutable")})
              </span>
            )}
          </label>
          <input
            type="text"
            value={hierarchyKey}
            disabled={mode === "edit"}
            onChange={(e) => setHierarchyKey(e.target.value)}
            placeholder="e.g. rotary_structure"
            maxLength={64}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t("hierarchyEditor.tiers")} ({orderedFieldGuids.length}/{MAX_TIERS})
          </label>

          {guidFields.length === 0 ? (
            <div className={styles.schemaEditorPlaceholder}>
              {t("hierarchyEditor.noGuidFields")}
            </div>
          ) : orderedFieldGuids.length === 0 ? (
            <div className={styles.schemaEditorPlaceholder}>
              {t("hierarchyEditor.noTiers")}
            </div>
          ) : (
            <ol
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
              }}
            >
              {orderedFieldGuids.map((fg, idx) => {
                const f = fieldByGuid[fg];
                const label = f
                  ? `${f.label} (${f.fieldKey})`
                  : t("hierarchyEditor.unknownField");
                return (
                  <li
                    key={fg}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      paddingBlock: 4,
                    }}
                  >
                    <span style={{ minInlineSize: 24, opacity: 0.7 }}>
                      {idx + 1}.
                    </span>
                    <span style={{ flex: 1 }}>{label}</span>
                    <button
                      type="button"
                      className={styles.schemaEditorBtn}
                      onClick={() => swap(idx, idx - 1)}
                      disabled={idx === 0}
                      aria-label={t("hierarchyEditor.moveUp")}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className={styles.schemaEditorBtn}
                      onClick={() => swap(idx, idx + 1)}
                      disabled={idx === orderedFieldGuids.length - 1}
                      aria-label={t("hierarchyEditor.moveDown")}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnDanger}`}
                      onClick={() => removeAt(idx)}
                      aria-label={t("hierarchyEditor.removeTier")}
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          {guidFields.length > 0 &&
            orderedFieldGuids.length < MAX_TIERS &&
            (eligibleToAdd.length > 0 ? (
              <div style={{ marginBlockStart: 8 }}>
                <select
                  value=""
                  onChange={(e) => {
                    addField(e.target.value);
                    e.target.value = "";
                  }}
                  aria-label={t("hierarchyEditor.addTier")}
                >
                  <option value="">
                    {t("hierarchyEditor.addTierPlaceholder")}
                  </option>
                  {eligibleToAdd.map((f) => (
                    <option key={f.fieldGuid} value={f.fieldGuid}>
                      {f.label} ({f.fieldKey})
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div
                className={styles.schemaEditorPlatformNote}
                style={{ marginBlockStart: 4 }}
              >
                {t("hierarchyEditor.allGuidFieldsUsed")}
              </div>
            ))}
        </div>

        <div className={styles.field}>
          <label className={styles.label}>
            {t("hierarchyEditor.presentation")}
          </label>
          {/* DEV-963 follow-up: stack radio options vertically (one per row).
              Reuses .schemaEditorCheckRow for label styling but overrides
              flex-direction to column so each option occupies its own line.
              Side-by-side caused awkward wrapping in the narrow modal. */}
          <div
            className={styles.schemaEditorCheckRow}
            style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 8 }}
          >
            <label>
              <input
                type="radio"
                name="presentation"
                value="cascading"
                checked={presentation === "cascading"}
                onChange={() => setPresentation("cascading")}
              />
              {t("hierarchyEditor.presentationCascading")}
            </label>
            <label>
              <input
                type="radio"
                name="presentation"
                value="unified_path"
                checked={presentation === "unified_path"}
                onChange={() => setPresentation("unified_path")}
              />
              {t("hierarchyEditor.presentationUnified")}
            </label>
          </div>
        </div>

        <div className={styles.schemaEditorModalFooter}>
          <button
            type="button"
            className={styles.schemaEditorBtn}
            onClick={onClose}
            disabled={saving}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className={`${styles.schemaEditorBtn} ${styles.schemaEditorBtnPrimary}`}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
